import type {QueryDocumentSnapshot} from 'firebase/firestore'
import {addDoc, collection, type DocumentReference, getDoc, serverTimestamp, Timestamp} from 'firebase/firestore'
import type {GoSchoolInvitation, GoSchoolUserRole, Organization} from '@goschool/model'
import {useAccepted, useCourseInstructorInvitations, useCourseStudentInvitations} from '@goschool/model'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  IconButton,
  Link,
  Stack,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import {Trans, useTranslation} from 'react-i18next'
import {type FormEvent, useCallback, useMemo, useState} from 'react'
import {GoSchool} from '@goschool/routing'
import {QRCode} from 'react-qrcode-logo'
import {typeConverter, useFirebaseAnalytics, useFirestore, useFirestoreSnapshot} from '@goschool/react-firebase'
import {useUserContext} from '@goschool/auth'
import {logEvent} from 'firebase/analytics'
import {useCourseContext} from '@goschool/dao'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {useRelTime} from '@goschool/i18n'
import QrCodeIcon from '@mui/icons-material/QrCode'
import UpdateIcon from '@mui/icons-material/Update'
import BlockIcon from '@mui/icons-material/Block'
import {useConfirm} from '@goschool/components'
import {GoSchoolDialog} from '@goschool/mui'
import {convertForm, validateForm} from '../admin/components/InvitationDialog'

interface CourseInvitationDialogProps {
  hide: () => void;
  isShown: boolean;
  invitation: QueryDocumentSnapshot<GoSchoolInvitation>;
}

export function QRInvitationDialog({hide, isShown, invitation}: CourseInvitationDialogProps) {
  const theme = useTheme()
  const isLG = useMediaQuery(theme.breakpoints.up('lg'))

  const size = isLG ? 512 : 256
  const {courseSnapshot} = useCourseContext()
  const course = useMemo(() => courseSnapshot.data(), [courseSnapshot])

  return <Dialog
    fullScreen={true} open={invitation?.exists() === true && isShown} maxWidth="xl" fullWidth={false}
    onClose={hide}>
    <DialogContent>
      <Stack direction="column" gap={2} alignItems="center" justifyContent="center" flexGrow={1} height="100%">
        <Typography flexGrow={0} variant="h6">
          <Trans i18nKey="auth:invitations.joinCourse" values={{courseTitle: course.title}}/></Typography>
        <InvitationQR invitation={invitation} size={size}/>
        <Box flexGrow={0}>
          <Button size="small" variant="outlined" onClick={hide}>
            <Trans i18nKey="auth:invitation.form.close"/>
          </Button>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>
}

function InvitationQR({invitation, size}: { invitation: QueryDocumentSnapshot<GoSchoolInvitation>, size: number }) {
  const url = useMemo(
    () => `${window.location.origin}${GoSchool.invitation(invitation.ref)}`, [invitation]
  )

  const theme = useTheme()


  return <QRCode
    size={size} qrStyle="fluid"
    logoImage="/site-logo.png" logoPaddingStyle="circle" removeQrCodeBehindLogo={true}
    logoWidth={32} logoHeight={32} logoPadding={2}
    bgColor={theme.palette.background.default}
    fgColor={theme.palette.primary.main}
    eyeColor={theme.palette.secondary.main}
    style={{height: 'auto', maxWidth: size, width: size}}
    value={url}
  />
}


export function StudentInvitation() {
  const {user} = useUserContext()
  const {courseSnapshot} = useCourseContext()

  const {
    invitation,
    createInvitation,
    extendInvitation,
    invalidateInvitation
  } = useCourseStudentInvitations(courseSnapshot, user)


  const confirm = useConfirm()
  const {t} = useTranslation()

  const confirmedInvalidate = useCallback(
    async () => {
      confirm(
        invalidateInvitation,
        t('auth:invitations.confirmInvalidateStudentInvitation'))
    }, [confirm, t, invalidateInvitation]
  )


  if (invitation === undefined) {
    return null
  }


  if (invitation == null) {
    return <CreateStudentInvitation createStudentInvitation={createInvitation}/>
  }

  return <InvitationLink
    invitation={invitation} extend={extendInvitation}
    invalidate={confirmedInvalidate}/>
}


function CreateStudentInvitation({createStudentInvitation}: {
  createStudentInvitation: (expirationInHours: number) => Promise<void>
}) {
  const {t} = useTranslation()

  return <FormControlLabel
    labelPlacement="top"
    label={t('auth:invitations.noStudentInvitation')}
    slotProps={{typography: {variant: 'body2', gutterBottom: true}}}

    control={<Button fullWidth={false} variant="contained" size="small"
      onClick={() => createStudentInvitation(168)}>
      <Trans
        i18nKey="auth:invitations.createStudentInvitation"/>
    </Button>}/>
}

interface InvitationLinkProps {
  invitation: QueryDocumentSnapshot<GoSchoolInvitation>
  extend: (expirationInHours: number) => Promise<void>;
  invalidate: () => Promise<void>;
}

export function InvitationLink({invitation, extend, invalidate}: InvitationLinkProps) {
  const url = useMemo(
    () => `${window.location.origin}${GoSchool.invitation(invitation.ref)}`, [invitation]
  )

  const {t} = useTranslation()
  const [qrDisplayed, setQrDisplayed] = useState(false)

  const copyUrl = useCallback(() => {
    navigator.clipboard.writeText(url)
    window.alert(t('auth:invitations.linkCopiedToClipboard'))
  }, [t, url])

  const expires_at = useRelTime(invitation.data().expires_at)
  return <Stack direction="column" alignItems="flex-start" justifyContent="center">
    <Typography variant="body2"><Trans
      i18nKey="auth:invitations.inviteLinkDescription"/></Typography>
    <Stack direction="row" alignItems="center">
      <Typography sx={{cursor: 'pointer'}} color="primary" variant="body2"
        onClick={copyUrl}>{GoSchool.invitation(invitation.ref)}</Typography>
      <IconButton onClick={copyUrl} color="primary">
        <ContentCopyIcon sx={{fontSize: '1rem'}}/>
      </IconButton>
      <IconButton color="primary" size="small" onClick={() => setQrDisplayed(true)}><QrCodeIcon
        sx={{fontSize: '1rem'}}/></IconButton>
      <IconButton edge="end" onClick={invalidate} color="error"><BlockIcon sx={{fontSize: '1rem'}}/></IconButton>
    </Stack>
    {expires_at != null &&
        <Stack direction="row" alignItems="center"><Typography variant="body2"><Trans
          i18nKey="auth:invitations.inviteLinkExpiresAt"
          values={{expires_at}}/>
        </Typography>

        <IconButton color="primary" title={t('auth:invitations.extendStudentInvitation')} onClick={() => extend(168)}><UpdateIcon
          sx={{fontSize: '1rem'}}/></IconButton>
        </Stack>
    }
    <QRInvitationDialog invitation={invitation} hide={() => setQrDisplayed(false)} isShown={qrDisplayed}/>
  </Stack>
}

export function useCourseInvitationGenerator(expirationInHours = 1) {
  const firestore = useFirestore()
  const {user, roles} = useUserContext()
  const {courseSnapshot} = useCourseContext()

  const invitationsCollection = useMemo(
    () => {
      return collection(firestore, 'invitations').withConverter(typeConverter<GoSchoolInvitation>())
    }, [firestore]
  )

  const [invitationRef, setInvitationRef] = useState<DocumentReference<GoSchoolInvitation> | null | undefined>(null)
  const invitation = useFirestoreSnapshot(invitationRef)
  const {analytics} = useFirebaseAnalytics()

  const createInvitation = useCallback(
    async () => {
      if (user == null) {
        throw new Error('User not authenticated')
      }
      if (courseSnapshot.ref.parent.parent == null) {
        throw new Error('Course not in organization')
      }
      if (invitation?.exists) {
        throw new Error('Invitation already exists')
      }
      setInvitationRef(undefined)
      try {
        const ref = await addDoc(invitationsCollection, {
          created_at: serverTimestamp(),
          created_by: user.uid,
          roles: ['student'],
          email: null,
          course: courseSnapshot.ref,
          organization: courseSnapshot.ref.parent.parent.withConverter(typeConverter<Organization>()),
          expires_at: Timestamp.fromMillis(Date.now() + 1000 * 60 * 60 * expirationInHours)
        })

        logEvent(analytics, 'invitation_created', {
          course_id: courseSnapshot.id,
          invitation_id: ref.id
        })

        setInvitationRef(ref)
      } catch (e) {
        console.error('cannot create invitation', e)
        console.dir({roles, user})
      }
    }, [user, courseSnapshot, invitation, invitationsCollection, expirationInHours, analytics, roles]
  )

  const reset = useCallback(() => {
    setInvitationRef(null)
  }, [])


  const result = useMemo(() => {
    if (invitationRef !== null || invitation?.exists) {
      return {invitation, isCreating: true}
    } else {
      return {invitation: null, isCreating: false}
    }
  }, [invitation, invitationRef])


  return {createInvitation, reset, ...result}
}


interface InvitationFormFields {
  email: string | null;
  name: string | null;
  message: string | null;
}


interface FieldErrors {
  email: string | null;
  name: string | null;
  message: string | null;
}

interface InstructorInvitationDialogProps {
  hide: () => void;
  isShown: boolean;
}

export function InstructorInvitationDialog({hide, isShown}: InstructorInvitationDialogProps) {
  const {user} = useUserContext()
  const {courseSnapshot} = useCourseContext()
  const {invitations} = useCourseInstructorInvitations(courseSnapshot, user)

  const {t} = useTranslation()

  const [fields, setFields] = useState<InvitationFormFields>({
    email: null,
    name: null,
    message: null
  })

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
    email: null,
    name: null,
    message: null
  })

  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)

  const setField = useCallback(
    (field: keyof InvitationFormFields) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFields((prev) => ({...prev, [field]: event.target.value}))
    }, []
  )
  const firestore = useFirestore()

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()

      setFieldErrors({
        email: null, name: null, message: null
      })
      setError(null)
      setSubmitState('submitting')
      try {
        const organizationRef = courseSnapshot.ref.parent.parent?.withConverter(typeConverter<Organization>()) ?? null
        const organization = organizationRef != null ? await getDoc(organizationRef) : null
        if (!organization?.exists()) {
          throw new Error('Organization not found')
        }
        const course = courseSnapshot
        const roles: GoSchoolUserRole[] = ['instructor']
        const submitFields = {
          ...fields,
          roles, organization, course
        }
        const validation = validateForm(submitFields, t)
        if (validation != null) {
          setFieldErrors(validation.fieldErrors)
          setError(validation.formError)
          return
        }

        if (user == null) {
          throw new Error('User not authenticated')
        }

        const invitation = {
          ...convertForm(submitFields),
          created_by: user.uid
        }
        await addDoc(collection(firestore, 'invitations'), invitation)
        setFields({email: null, name: null, message: null})
      } finally {
        setSubmitState('idle')
      }
    }, [courseSnapshot, fields, firestore, t, user]
  )

  return <GoSchoolDialog
    open={isShown} maxWidth="lg" fullWidth={true}
    onClose={hide}>
    <DialogTitle><Trans i18nKey="auth:invitations.instructorInvitation"/></DialogTitle>
    <DialogContent>
      <TextField
        margin="normal"
        size="small" fullWidth={true}
        error={fieldErrors.name != null} helperText={fieldErrors.name}
        label={t('auth:invitation.form.fields.name')} value={fields.name ?? ''}
        onChange={setField('name')}/>

      <TextField
        margin="normal" type="email"
        size="small" fullWidth={true}
        error={fieldErrors.email != null} helperText={fieldErrors.email}
        label={t('auth:invitation.form.fields.email')} value={fields.email ?? ''}
        onChange={setField('email')}/>

      <FormHelperText error={true}>{error}</FormHelperText>

      <Stack direction="row" gap={2} justifyContent="flex-start" flexWrap="wrap" pt={2}>{
        (invitations ?? []).map(
          (snapshot) => <InvitationChip key={snapshot.id} snapshot={snapshot}/>
        )
      }</Stack>
    </DialogContent>
    <DialogActions>
      <Button size="small" type="submit" variant="contained" color="primary"
        loading={submitState === 'submitting'}
        autoFocus={true} onClick={submit}>
        <Trans i18nKey="auth:invitation.form.submit"/>
      </Button>
      <Button variant="outlined" onClick={hide} size="small"><Trans
        i18nKey="auth:invitation.form.close"/></Button>
    </DialogActions>
  </GoSchoolDialog>
}

function InvitationChip({snapshot}: { snapshot: QueryDocumentSnapshot<GoSchoolInvitation> }) {
  const accepted = useAccepted(snapshot)
  const invitation = useMemo(() => snapshot.data(), [snapshot])
  const expires_at = useRelTime(invitation.expires_at)

  if (accepted != null && accepted.length > 0) {
    return null
  }

  if (invitation.expires_at != null && invitation.expires_at.toMillis() < Date.now()) {
    return null
  }

  return <InvitationChipStyled variant="outlined" size="small" color="primary"
    label={<>
      <Typography variant="body2">{invitation.name}</Typography>
      {invitation.email != null
        ? <Link href={`mailto:${invitation.email}`}>{invitation.email}</Link>
        : null}
      <Typography variant="body2"><Trans
        i18nKey="auth:invitations.inviteLinkExpiresAt"
        values={{expires_at}}/>
      </Typography>
    </>}/>

}

const InvitationChipStyled = styled(Chip, {name: 'InvitationChip'})(
  ({theme}) => ({
    height: 'auto',
    '& .MuiChip-label': {
      padding: theme.spacing(2),
      display: 'block',
      whiteSpace: 'normal'
    }
  })
)
